import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, X, ArrowRight, RotateCcw, Volume2 } from "lucide-react";
import { useAudience } from "./NetworkAtoms";

/**
 * The explainer film. A quiet pill anywhere on the page opens a cinema overlay that grows out
 * of the pill it was clicked from, plays with sound, and offers the next step when it ends.
 * Hosted on YouTube for adaptive quality (a 4K master would cripple a phone), wrapped in our
 * own chrome so the visitor never leaves Connexis.
 */
const VIDEO_ID = "dSnPoeUGPPg";
const POSTER = `https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`;
const POSTER_FALLBACK = `https://i.ytimg.com/vi/${VIDEO_ID}/hqdefault.jpg`;

const ExplainerCtx = createContext({ open: () => {} });
export const useExplainer = () => useContext(ExplainerCtx);

function viewerId() {
  try {
    let v = localStorage.getItem("cx_viewer");
    if (!v) {
      v = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("cx_viewer", v);
    }
    return v;
  } catch { return "anon"; }
}

function track(event) {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return;
  try {
    fetch(`${base}/api/hub/video/track`, {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ video_id: VIDEO_ID, event, viewer: viewerId(),
                             page: window.location.pathname.slice(0, 120) }),
    }).catch(() => {});
  } catch { /* never let analytics get in the way of the film */ }
}

export function ExplainerProvider({ children, audience }) {
  const [origin, setOrigin] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback((rect) => {
    setOrigin(rect || null);
    setIsOpen(true);
    track("open");
  }, []);

  return (
    <ExplainerCtx.Provider value={{ open }}>
      {children}
      <ExplainerLightbox open={isOpen} origin={origin} audienceOverride={audience}
                         onClose={() => setIsOpen(false)} />
    </ExplainerCtx.Provider>
  );
}

/** The pill. `inline` is the quieter, in-body version; `full` matches the CTA buttons on a phone. */
export function WatchExplainerButton({ inline = false, full = false, className = "", testId = "watch-explainer" }) {
  const { open } = useExplainer();
  const reduce = useReducedMotion();
  const ref = useRef(null);

  const handle = () => open(ref.current?.getBoundingClientRect());

  return (
    <button ref={ref} onClick={handle} data-testid={testId}
            className={`group relative inline-flex items-center gap-3 rounded-full border border-white/12 bg-white/[0.04] hover:bg-white/[0.07] hover:border-white/25 transition-colors backdrop-blur-sm ${
              inline ? "h-10 pl-1.5 pr-4" : "h-12 sm:h-11 pl-1.5 pr-5"} ${
              full ? "w-full justify-center sm:w-auto sm:justify-start" : ""} ${className}`}>
      <span className="relative grid place-items-center size-8 rounded-full bg-[#0055FF] text-white shrink-0">
        <Play className="size-3.5 translate-x-[1px]" fill="currentColor" />
        {!reduce && (
          <span className="absolute inset-0 rounded-full border border-[#0055FF] animate-ping opacity-60"
                style={{ animationDuration: "2.4s" }} aria-hidden />
        )}
      </span>
      <span className={`text-white font-medium whitespace-nowrap ${inline ? "text-[13px]" : "text-[13.5px]"}`}>
        Watch our explainer
      </span>
      <span className="hidden sm:block text-[11px] text-slate-500 group-hover:text-slate-400 transition-colors">
        4 min
      </span>
    </button>
  );
}

function useYouTubeApi(active) {
  const [ready, setReady] = useState(() => Boolean(window.YT?.Player));
  useEffect(() => {
    if (!active || ready) return;
    if (window.YT?.Player) { setReady(true); return; }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { if (typeof prev === "function") prev(); setReady(true); };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    }
  }, [active, ready]);
  return ready;
}

function ExplainerLightbox({ open, origin, onClose, audienceOverride }) {
  const ctx = useAudience();
  const audience = audienceOverride || ctx.audience;
  const reduce = useReducedMotion();
  const apiReady = useYouTubeApi(open);
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [ended, setEnded] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);

  // Escape to close, and don't let the page scroll behind the film
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prevOverflow; };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !apiReady || !hostRef.current || playerRef.current) return;
    setEnded(false);
    setNeedsTap(false);
    playerRef.current = new window.YT.Player(hostRef.current, {
      videoId: VIDEO_ID,
      playerVars: { autoplay: 1, playsinline: 1, rel: 0, modestbranding: 1, iv_load_policy: 3, color: "white" },
      events: {
        onReady: (e) => {
          try { e.target.unMute(); e.target.setVolume(100); e.target.playVideo(); } catch { /* handled below */ }
          setTimeout(() => {
            try { if (playerRef.current?.getPlayerState?.() !== 1) setNeedsTap(true); } catch { setNeedsTap(true); }
          }, 1500);
        },
        onStateChange: (e) => {
          if (e.data === 1) setNeedsTap(false);
          if (e.data === 0) { setEnded(true); track("complete"); }
        },
      },
    });
  }, [open, apiReady]);

  // Closing must stop the sound, always
  useEffect(() => {
    if (open) return;
    try { playerRef.current?.destroy?.(); } catch { /* already gone */ }
    playerRef.current = null;
    setEnded(false);
    setNeedsTap(false);
  }, [open]);

  const grow = origin
    ? {
        x: origin.left + origin.width / 2 - window.innerWidth / 2,
        y: origin.top + origin.height / 2 - window.innerHeight / 2,
        scale: 0.2,
      }
    : { x: 0, y: 0, scale: 0.9 };

  const cta = audience === "lender"
    ? { to: "/lender/apply", label: "Apply as a lender" }
    : { to: "/introducer/apply", label: "Apply as an introducer" };

  const replay = () => {
    setEnded(false);
    try { playerRef.current?.seekTo(0); playerRef.current?.playVideo(); } catch { /* nothing to replay */ }
  };

  const tapToPlay = () => {
    try { playerRef.current?.unMute(); playerRef.current?.setVolume(100); playerRef.current?.playVideo(); } catch { /* ignore */ }
    setNeedsTap(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.28 }}
          data-testid="explainer-lightbox"
        >
          <motion.button
            onClick={onClose} aria-label="Close the film"
            className="absolute inset-0 bg-[#040711]/95 backdrop-blur-xl cursor-zoom-out"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="explainer-backdrop"
          />

          <motion.div
            className="relative w-full max-w-[1180px]"
            initial={reduce ? { opacity: 0 } : { opacity: 0, ...grow }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, ...grow }}
            transition={reduce ? { duration: 0.2 } : { type: "spring", stiffness: 210, damping: 26 }}
          >
            <div className="flex items-center justify-between gap-3 mb-2.5 sm:mb-3">
              <div className="min-w-0 flex items-center gap-2.5">
                <span className="size-1.5 rounded-full bg-[#0055FF] shrink-0" />
                <span className="font-mono text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.18em] text-slate-400 truncate">
                  Connexis Lending &amp; Introducer Network
                </span>
              </div>
              <button onClick={onClose}
                      className="grid place-items-center size-9 rounded-full border border-white/12 bg-white/[0.04] text-slate-300 hover:text-white hover:border-white/30 transition-colors shrink-0"
                      data-testid="explainer-close" aria-label="Close">
                <X className="size-4" />
              </button>
            </div>

            <div className="relative w-full aspect-video overflow-hidden rounded-xl sm:rounded-2xl border border-white/10 bg-black shadow-[0_40px_120px_-30px_rgba(0,0,0,1)]">
              <div ref={hostRef} className="absolute inset-0 size-full" data-testid="explainer-player" />

              {needsTap && !ended && (
                <button onClick={tapToPlay} data-testid="explainer-tap-to-play"
                        className="absolute inset-0 grid place-items-center bg-black/45 backdrop-blur-[2px]">
                  <span className="inline-flex items-center gap-2.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] transition-colors pl-4 pr-5 h-12 text-white text-[14px] font-semibold">
                    <Volume2 className="size-4" /> Tap to play with sound
                  </span>
                </button>
              )}

              <AnimatePresence>
                {ended && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 grid place-items-center bg-[#050A16]/95 backdrop-blur-md px-5 text-center"
                    data-testid="explainer-endcard"
                  >
                    <div>
                      <p className="font-cabinet text-[20px] sm:text-[26px] font-semibold text-white tracking-[-0.02em]">
                        That's the whole thing.
                      </p>
                      <p className="mt-2 text-[13px] sm:text-[14px] text-slate-400 max-w-md mx-auto">
                        Membership is by application — we read every one personally.
                      </p>
                      <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                        <Link to={cta.to} onClick={onClose} data-testid="explainer-endcard-cta"
                              className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[14.5px] font-semibold transition-colors">
                          {cta.label}
                          <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                        </Link>
                        <button onClick={replay} data-testid="explainer-replay"
                                className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full border border-white/15 hover:border-white/35 text-white text-[14px] font-medium transition-colors">
                          <RotateCcw className="size-4" /> Watch again
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <p className="mt-2.5 text-center text-[11px] text-slate-500">
              <span className="sm:hidden">Tap the full-screen icon in the player for the big version · tap outside to close</span>
              <span className="hidden sm:inline">
                Press <span className="font-mono text-slate-400">Esc</span> or tap outside to close
              </span>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Poster used by the inline card in the "how it works" band. */
export function ExplainerPoster({ className = "" }) {
  const { open } = useExplainer();
  const ref = useRef(null);
  const [src, setSrc] = useState(POSTER);
  return (
    <button ref={ref} onClick={() => open(ref.current?.getBoundingClientRect())}
            data-testid="watch-explainer-poster"
            className={`group relative block w-full overflow-hidden rounded-2xl border border-white/10 bg-black/40 ${className}`}>
      <img src={src} onError={() => setSrc(POSTER_FALLBACK)} alt="" aria-hidden
           className="w-full aspect-video object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
      <span className="absolute inset-0 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/20 to-transparent" />
      <span className="absolute inset-0 grid place-items-center">
        <span className="grid place-items-center size-14 rounded-full bg-[#0055FF] text-white shadow-[0_10px_40px_-8px_rgba(0,85,255,0.9)] transition-transform duration-300 group-hover:scale-110">
          <Play className="size-5 translate-x-[1px]" fill="currentColor" />
        </span>
      </span>
    </button>
  );
}
